import { AppPageLayout } from "@/components/common/AppPageLayout";
import { useSelectFormById } from "@/features/forms/api/selectFormById";
import { useActionCopy } from "@/features/i18n/actionCopy";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import {
    formPageRouteShellClass,
    formPageShellFillClass,
    publicFormRoutePaddingClass,
} from "@/features/pages/components/formPageLayout";
import { useFormNavigationStore } from "@/features/pages/hooks/useFormNavigationStore";
import { formNameLabel } from "@/features/navigation/services/entityDisplayLabel";
import { useValidationByIdQuery } from "@/features/validations/api/selectValidationById";
import AdminOnBehalfCallout from "@/features/validations/components/AdminOnBehalfCallout";
import ValidationDecisionFooter from "@/features/validations/components/ValidationDecisionFooter";
import { assignedValidatorLabel, assignedValidatorName } from "@/features/validations/utils/assignedValidatorLabel";
import { lastReviewPageIndex } from "@/features/validations/utils/lastReviewPageIndex";
import { readValidationFormSession } from "@/features/validations/utils/validationFormSession";
import { readValidationReviewResults } from "@/features/validations/utils/validatorAutoSubmitEmail";
import { useAuth } from "@/utils/auth";
import { useNav } from "@/utils/nav";
import { FormResultsBody } from "@patient-watch/forms";
import { ui } from "@patient-watch/ui";
import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

const ValidationFormResults = () => {
    const { validationId, formId } = Route.useParams();
    const navigate = useNavigate();
    const copy = useActionCopy();
    const auth = useAuth();
    const { locale } = useAppLocale();
    const { data: validation, isLoading } = useValidationByIdQuery(validationId);
    const { data: form } = useSelectFormById(formId, locale);
    const { reviewEntryValues: entryValues } = Route.useRouteContext();
    const [returning, setReturning] = useState(false);
    const isOnBehalf = Boolean(validation && auth.roles.isAdmin && validation.validated_by !== auth.id);
    const assignedName = assignedValidatorName(validation?.validated_by_name, validation?.validated_by_email);
    const assignedLabel = assignedValidatorLabel(validation?.validated_by_name, validation?.validated_by_email);
    const formName = form?.form_name ?? validation?.entity_name;

    useNav([
        { label: "Validate", route: { to: "/validate" } },
        {
            label: formNameLabel(form, validation?.entity_name || "Review"),
            route: { to: "/validate/$validationId/forms/$formId", params: { validationId, formId } },
        },
        {
            label: "Results",
            route: { to: "/validate/$validationId/forms/$formId/results", params: { validationId, formId } },
        },
    ]);

    const goToLastPage = () => {
        const session = readValidationFormSession(validationId, formId);
        const history = session ? useFormNavigationStore.getState().getHistory(session.entryId) : [];
        const pageIndex = lastReviewPageIndex(form?.pages, history);
        void navigate({
            to: "/validate/$validationId/forms/$formId/page/$pageIndex",
            params: { validationId, formId, pageIndex: String(pageIndex) },
        });
    };

    if (isLoading) {
        return <p className="mx-auto p-4 w-full max-w-4xl text-muted-foreground text-sm">Loading results...</p>;
    }

    // Once a sign-off is sent the validation refetches as actioned. Keep the results on screen
    // while we head back to the list rather than flashing "already actioned".
    if (!validation || (validation.is_valid !== null && !returning)) {
        return (
            <div className="mx-auto p-4 w-full max-w-4xl text-center">
                <p className="mb-4 text-muted-foreground">This item has already been actioned or no longer exists.</p>
                <ui.Button asChild variant="outline">
                    <Link to="/validate">Back to your list</Link>
                </ui.Button>
            </div>
        );
    }

    return (
        <AppPageLayout width="md" padding="sm" className={`${publicFormRoutePaddingClass} ${formPageRouteShellClass}`}>
            <div className={formPageShellFillClass}>
                <div className="flex flex-col flex-1 gap-4 min-w-0">
                    {isOnBehalf ? (
                        <AdminOnBehalfCallout assignedName={assignedName} assignedLabel={assignedLabel} />
                    ) : null}
                    <div>
                        <h1 className="mb-2 font-bold text-slate-900 dark:text-slate-100 text-2xl">
                            {formName || "Form"} Results
                        </h1>
                        <p className="mb-4 text-slate-600 dark:text-slate-300 text-sm">Your submission summary</p>
                        {entryValues.length > 0 ? (
                            <FormResultsBody entryValues={entryValues} />
                        ) : (
                            <p className="text-muted-foreground text-sm">No answers were saved for this review.</p>
                        )}
                    </div>
                </div>

                <ValidationDecisionFooter
                    validationId={validation.validation_id}
                    isOnBehalf={isOnBehalf}
                    assignedName={assignedName}
                    assignedLabel={assignedLabel}
                    backLabel={copy.back}
                    onBack={goToLastPage}
                    onReturningChange={setReturning}
                />
            </div>
        </AppPageLayout>
    );
};

export const Route = createFileRoute("/_app/validate/$validationId/forms/$formId/results")({
    component: ValidationFormResults,
    // Answers only live in this tab's sessionStorage. Without them there is nothing to show,
    // so go back to the form cover before rendering instead of waiting on an effect.
    beforeLoad: ({ params }) => {
        const reviewEntryValues = readValidationReviewResults(params.validationId);
        if (!reviewEntryValues) {
            throw redirect({
                to: "/validate/$validationId/forms/$formId",
                params: { validationId: params.validationId, formId: params.formId },
                replace: true,
            });
        }
        return { reviewEntryValues };
    },
});
